import React, { useState, useEffect } from "react";
import styles from "./YesNoScoreComponent.module.scss";
import { InputLabel, TextField, Tooltip } from "@mui/material";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useDispatch } from "react-redux";
import { setWeight } from "redux/slices/scoringSlice";
import { capitalizeFirstLetter, truncateText } from "utils/utils";
import toast from "react-hot-toast";

const YesNoScoreComponent = ({ handleDelete, item, initialValues = {} }) => {
  const dispatch = useDispatch();

  // Initialize scores with "Unanswered" only if it doesn’t already exist
  const [scores, setScores] = useState(() => {
    const options = item?.options || item?.option || [];
    const updatedOptions = new Set([...options, "Unanswered"]); // Ensures uniqueness
    return Object.fromEntries([...updatedOptions].map((value) => [value, 0]));
  });

  const modifiedOptionsWithWeightAndMaxWeight = (updatedScores) => {
    const options = Object.entries(updatedScores).reduce((acc, [option, score]) => {
      if (!acc.some((o) => o.option === option)) {
        acc.push({
          option: capitalizeFirstLetter(option),
          score: Number(score),
        });
      }
      return acc;
    }, []);

    const maxWeight = options.reduce((max, current) => (current.score > max ? current.score : max), -Infinity);
    return { maxWeight, options };
  };

  const handleInputChange = (field, value) => {
    if (value === "" || isNaN(value)) {
      setScores((prev) => ({ ...prev, [field]: "" })); // Allow temporary empty input
      return;
    }

    const numericValue = Number(value);
    const updatedScores = { ...scores, [field]: numericValue };
    setScores(updatedScores);

    const { options, maxWeight } = modifiedOptionsWithWeightAndMaxWeight(updatedScores);

    dispatch(
      setWeight({
        type: "field",
        fieldId: item?.fieldId,
        scoreType: "byOptions",
        options: [...options],
        maxWeight: maxWeight,
      })
    );
  };



  useEffect(() => {
    if (!item?.options?.length) {
      const { options, maxWeight } = modifiedOptionsWithWeightAndMaxWeight(scores);

      dispatch(
        setWeight({
          type: "field",
          fieldId: item?.fieldId,
          scoreType: "byOptions",
          options: options.filter((opt) => opt.option !== "Unanswered"), // Ensure "Unanswered" is not duplicated
          maxWeight: maxWeight,
        })
      );
    } else {
      let temp = item?.options?.reduce((acc, cr) => {
        acc[cr.option] = cr?.score || "0";
        return acc;
      }, {});

      setScores(temp);
    }
  }, [item]);

  return (
    <div className={styles?.scoreContainer}>
      <div className={styles?.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Tooltip title={item?.name}>
            <span>{truncateText(item?.name || "", 50)}</span>
          </Tooltip>
          <Tooltip title={"Assign a value to each choice below."}>
            <InfoOutlinedIcon fontSize="small" sx={{ color: "#3e9ef7", cursor: "pointer" }} />
          </Tooltip>
        </div>
        <span style={{ cursor: "pointer" }} onClick={() => handleDelete(item.id, item?.fieldId)}>
          <HighlightOffOutlinedIcon sx={{ color: "#3e9ef7" }} fontSize="medium" />
        </span>
      </div>

      <div className={styles?.options}>
        {item?.options?.length
          ? item?.options?.map((option) => (
            <div key={option?.option}>
              <InputLabel>{option?.option}</InputLabel>
              <TextField
                size="small"
                type="number"
                value={scores[option?.option]}
                onChange={(e) => handleInputChange(option?.option, e.target.value)}
                onBlur={(e) => {
                  if (e.target.value === "") {
                    handleInputChange(option?.option, 0); // Set to 0 only if empty
                  }
                }}
              />
            </div>
          ))
          : item?.option?.map((option) => (
            <div key={option}>
              <InputLabel>{option}</InputLabel>
              <TextField
                size="small"
                type="number"
                value={scores[option]}
                onChange={(e) => handleInputChange(option, e.target.value)}
                onBlur={(e) => {
                  if (e.target.value === "") {
                    handleInputChange(option?.option, 0); // Set to 0 only if empty
                  }
                }}
              />
            </div>
          ))}

        {!item?.options?.some((opt) => opt.option === "Unanswered") && (
          <div>
            <InputLabel>Unanswered</InputLabel>
            <TextField
              size="small"
              type="number"
              value={scores["Unanswered"]}
              onChange={(e) => handleInputChange("Unanswered", e.target.value)}
              onBlur={(e) => {
                if (e.target.value === "") {
                  handleInputChange("Unanswered", 0); // Set to 0 only if empty
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default YesNoScoreComponent;
